/**
 * FEEDBACK WIDGET — NETWORK TRACKER (TICKET-CRM-14)
 * Wrap de window.fetch pour tracer les requêtes API dans le snapshot debug.
 * Port de benevoles avec redaction RENFORCÉE : le CRM manipule des données
 * perso (contacts, paiements) et les URLs PostgREST embarquent des filtres
 * type ?email=eq.x — la query string entière est remplacée par ?[redacted]
 * AVANT stockage dans le buffer (jamais de donnée perso en mémoire tracker).
 */

import type { NetworkRequest } from './feedback.types';

const MAX_REQUESTS = 30;

// Assets et bruit outillage (Vite) — pas des appels API, on ne les trace pas.
const SKIP_PATTERNS = [
  /\.(js|css|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot|map)(\?|$)/i,
  /^\/favicon/,
  /^\/assets\//,
  /^\/@vite/,
  /^\/node_modules\//,
  /hot-update/,
];

export function redactUrl(rawUrl: string, currentOrigin: string): string {
  try {
    const parsed = new URL(rawUrl, currentOrigin);
    const redactedSearch = parsed.search ? '?[redacted]' : '';
    if (parsed.origin === currentOrigin) {
      return parsed.pathname + redactedSearch;
    }
    return parsed.origin + parsed.pathname + redactedSearch;
  } catch {
    // URL non parsable : on coupe quand même tout ce qui suit le ?
    const queryIndex = rawUrl.indexOf('?');
    return queryIndex === -1 ? rawUrl : `${rawUrl.slice(0, queryIndex)}?[redacted]`;
  }
}

class NetworkTracker {
  private requests: NetworkRequest[] = [];
  private isInitialized = false;
  private originalFetch: typeof fetch | null = null;

  initialize(): void {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    // Référence BRUTE (pas de bind) pour que destroy() restaure l'identité
    // exacte ; l'appel se fait via .call(window) pour garder le bon this.
    this.originalFetch = window.fetch;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const startTime = Date.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = (init?.method || 'GET').toUpperCase();

      try {
        const response = await this.originalFetch!.call(window, input, init);
        this.addRequest({
          url: redactUrl(url, window.location.origin),
          method,
          status: response.status,
          duration: Date.now() - startTime,
          timestamp: new Date().toISOString(),
        });
        return response;
      } catch (error) {
        this.addRequest({
          url: redactUrl(url, window.location.origin),
          method,
          status: 0,
          duration: Date.now() - startTime,
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    };

    this.isInitialized = true;
  }

  destroy(): void {
    if (!this.isInitialized || !this.originalFetch || typeof window === 'undefined') {
      return;
    }
    window.fetch = this.originalFetch;
    this.originalFetch = null;
    this.isInitialized = false;
  }

  private addRequest(request: NetworkRequest): void {
    if (SKIP_PATTERNS.some((pattern) => pattern.test(request.url))) {
      return;
    }
    this.requests.push(request);
    if (this.requests.length > MAX_REQUESTS) {
      this.requests.shift();
    }
  }

  getRequests(): NetworkRequest[] {
    return [...this.requests];
  }

  clear(): void {
    this.requests = [];
  }

  isActive(): boolean {
    return this.isInitialized;
  }
}

export const networkTracker = new NetworkTracker();
